/* eslint-disable */
import store from '@/store';
import router from '@/router/config_routers';
import { CLIENT_BRANDING } from "@/utils/system_constants/constant";
import { install_session_expiry_handler } from '@/utils/auth/session_manager';
import axios from 'axios';
import { createApp } from 'vue';
import App from './App.vue';
import 'bootstrap/dist/css/bootstrap.min.css';
import '@/assets/css/global.css';

// Set axios defaults
axios.defaults.withCredentials = true;

const configured_client_domain = process.env.VUE_APP_CLIENT_DOMAIN;
const browser_client_domain = window.CLIENT_DOMAIN;
const runtime_domain = browser_client_domain && browser_client_domain !== 'localhost'
  ? browser_client_domain
  : configured_client_domain || window.location.hostname;
const isValidDomain = Boolean(runtime_domain && runtime_domain !== 'localhost');

const app = createApp(App, { isValidDomain });
app.use(store);
app.use(router);
install_session_expiry_handler({ router, store });

// --------------------
// Dynamic Branding Logic
// --------------------

// fetch logos from frontend
const logos = {
  'x-verdict': require('@/assets/images/x-verdict/logos/x_verdict_sidebar.png'),
  'remat.x-verdict': require('@/assets/images/remat/logos/remat_holdings_logo.png'),

};

const images = {
  'x-verdict': require('@/assets/images/x-verdict/pictures/onboarding_x-verdict.png'),
  'remat.x-verdict': require('@/assets/images/remat/pictures/remat_onboarding.png'),

};

const form_backgrounds = {};

const favicons = {
  'x-verdict': require('@/assets/images/x-verdict/logos/x_verdict_favicon_logo.png'),
  'remat.x-verdict': require('@/assets/images/remat/logos/remat_holdings_logo.png'),

};

const default_branding_key = 'x-verdict';
const default_logo = Reflect.get(logos, default_branding_key);
const default_image = Reflect.get(images, default_branding_key);

app.config.globalProperties.$clientLogo = default_logo;
app.config.globalProperties.$clientImage = {
  onboarding: default_image,
  background: default_image
};


// Apply branding colors dynamically to CSS variables
const apply_branding_colors = (colors) => {
  const root = document.documentElement;
  const colorMap = {
    primary: '--primary-color',
    secondary: '--secondary-color',
    tertiary_colour: '--tertiary-color',
    accent: '--accent-color',
    highlight: '--highlight-color',
    new: '--new-color',
    text: '--text-color',
    gradient: '--gradient'
  };

  Object.entries(colorMap).forEach(([key, cssVar]) => {
    const value = Reflect.get(colors, key);
    if (value) {
      root.style.setProperty(cssVar, value);
    }
  });

  if (colors?.gradient) {
    root.style.setProperty('--entity-icon-gradient', colors.gradient);
  }

  if (colors?.secondary) {
    root.style.setProperty('--color-x-verdict', `color-mix(in srgb, ${colors.secondary} 22%, transparent)`);
  }

  if (colors?.highlight) {
    root.style.setProperty('--color-x-verdict-border', `color-mix(in srgb, ${colors.highlight} 45%, transparent)`);
  }
};

const environment_prefixes = ['uat', 'stage', 'staging', 'demo'];
const second_level_domains = ['ac', 'co', 'com', 'gov', 'net', 'org'];

const get_clean_domain = () => {
  const explicit_client_domain = get_explicit_client_domain();
  if (explicit_client_domain) {
    return explicit_client_domain;
  }

  const parts = hostname_parts(runtime_domain);

  if (is_localhost(parts)) {
    return normalize_client_key(configured_client_domain) || default_branding_key;
  }

  remove_environment_prefix(parts);
  remove_public_suffix(parts);
  return parts.join('.') || normalize_client_key(configured_client_domain) || default_branding_key;
};

function is_localhost(parts) {
  return parts.length === 1 && parts[0] === 'localhost';
}

function get_explicit_client_domain() {
  const normalized_browser_domain = normalize_client_key(browser_client_domain);
  if (normalized_browser_domain && normalized_browser_domain !== 'localhost') {
    return normalized_browser_domain;
  }

  const normalized_configured_domain = normalize_client_key(configured_client_domain);

  if (is_localhost(hostname_parts(window.location.hostname))) {
    return normalized_configured_domain;
  }

  return '';
}

function hostname_parts(domain) {
  return String(domain || '')
    .split(':')[0]
    .toLowerCase()
    .split('.')
    .filter(Boolean);
}

function normalize_client_key(client_key) {
  return String(client_key || '')
    .trim()
    .toLowerCase()
    .replace(/^["']|["']$/g, '');
}

function remove_environment_prefix(parts) {
  if (environment_prefixes.includes(parts[0])) {
    parts.shift();
  }
}

function remove_public_suffix(parts) {
  parts.pop();
  if (parts.length > 1 && second_level_domains.includes(parts[parts.length - 1])) {
    parts.pop();
  }
}

// Set client branding globally on the Vue app instance
const set_client_branding = (branding_data) => {
  if (branding_data?.colors) {
    apply_branding_colors(branding_data.colors);
  }

  const domain_key = get_clean_domain();
  const selected_logo = Reflect.get(logos, domain_key) || default_logo;
  const selected_image = Reflect.get(images, domain_key) || default_image;
  const selected_bg = Reflect.get(form_backgrounds, domain_key) || selected_image;

  // Set logo globally
  app.config.globalProperties.$clientLogo = selected_logo;

  // Set favicon dynamically (prefer favicon, fallback to logo)
  const favicon = document.querySelector('link[rel="icon"]');
  const selected_favicon = Reflect.get(favicons, domain_key);
  const selected_icon = selected_favicon || selected_logo; // prefer favicon, fallback to logo

  if (favicon && selected_icon) {
    favicon.href = selected_icon;
  }


  // Optional: add other assets
  app.config.globalProperties.$clientImage = {
    onboarding: selected_image,
    background: selected_bg
  };
};

// Fetch branding from backend API and apply it
const fetch_branding = () => {
  const domain = get_clean_domain();

  return axios.get(CLIENT_BRANDING, {
    params: { domain },
    skipSessionExpiryHandler: true
  })
    .then(response => {
      set_client_branding(response.data || {});
    })
    .catch(() => undefined);
};

// --------------------
// Mount app after fetching branding
// --------------------
fetch_branding().finally(() => {
  app.mount('#app');
});
